// Ordering comparison for DynamoDB stream SequenceNumbers.
//
// Stream SequenceNumbers are decimal strings of arbitrary length (they can
// exceed 20+ digits), so numeric parsing is unsafe — Number() would lose
// precision. Because they are non-negative decimals with no leading zeros, a
// longer string is always the larger number; equal-length strings compare
// correctly lexicographically. Equal sequences are a duplicate delivery, so
// the incoming one is not newer.
//
// Asymmetric handling of a missing sequence: after the backend fix, the only
// unsequenced `data_change` source is the current_pointer relay — pure
// redundancy, since the same record also arrives via sequenced deployment
// events. So an unsequenced incoming update against a sequenced stored record
// is dropped: applying a late relay would revert the row to a stale state with
// no corrective follow-up during a terraform review gate (which can sit for
// minutes), whereas dropping it loses nothing. A missing stored sequence still
// means we can't order the change, so we apply (an entirely un-sequenced
// backend never sets `stored`, so everything applies).
//
// Backward compat: an entirely un-sequenced backend never sets `stored`, so
// everything applies. A backend rollback after records got sequenced drops
// updates until refresh/reconnect — sequence state is client-memory only.

/**
 * Whether `incoming` represents a strictly newer stream position than `stored`.
 * Missing `stored` ⇒ true (can't order, apply). Missing `incoming` against a
 * sequenced `stored` ⇒ false (unsequenced update never clobbers a sequenced
 * record). Equal ⇒ false (duplicate delivery).
 */
export const isNewerSequence = (incoming?: string, stored?: string): boolean => {
  if (!stored) return true;
  if (!incoming) return false;
  if (incoming.length !== stored.length) return incoming.length > stored.length;
  return incoming > stored;
};
